import React from "react";
import { Breadcrumb } from "../../../../atoms/breadcrumb";
import { useTableHooks } from "../../../../atoms/table/useTableHooks";
import { hasPermission, pascalToSnakeCase } from "../../../../../utils";
import {
  useLicenseListQuery,
} from "../../../../../store/license/licenseSlice";
import { Table } from "../../../../atoms/table";
import { Input } from "../../../../atoms/input";
import { toast } from "react-toastify";
import { useSyncUserMutation } from "../../../../../store/saasTool/saasToolSlice";
import {
  PAGE_NAME,
  PERMISSIONS,
  ROUTE,
  SOMETHING_WENT_WRONG,
} from "../../../../../constants/constants";
import { useNavigate } from "react-router-dom";

const formatColumnName = (name: string) => {
  switch (name) {
    case "totalLicenses":
      return "total";
    case "updatedAtTime":
      return "updatedAt";
    default:
      return name;
  }
};
const LicenseList = () => {
  const pageClassName = "p-licenseList";
  const navigate = useNavigate();

  const [syncUserApi, { isLoading: syncLoading }] = useSyncUserMutation();

  const tableHeader = [
    {
      title: "Serial",
      dataIndex: "id",
      isSortable: true,
    },
    {
      title: "Saas Name",
      dataIndex: "name",
      isSortable: true,
    },
    {
      title: "Subscriptions Type",
      dataIndex: "subscriptionType",
      isSortable: true,
    },
    {
      title: "Renewal Date",
      dataIndex: "renewalDate",
      isSortable: true,
    },
    {
      title: "Total Licenses",
      dataIndex: "totalLicenses",
      isSortable: true,
      permission: hasPermission(PERMISSIONS.LICENSE_EDIT.title),
    },
    {
      title: "Active",
      dataIndex: "active",
      isSortable: true,
    },
    {
      title: "Invited",
      dataIndex: "invited",
      isSortable: true,
    },
    {
      title: "Predicted",
      dataIndex: "predicted",
      isSortable: true,
    },
    {
      title: "Remaining",
      dataIndex: "remaining",
      isSortable: true,
    },
    {
      title: "Updated At",
      dataIndex: "updatedAtTime",
      isSortable: true,
    },
  ];

  const { search, query, sortHandler, searchHandler } = useTableHooks();

  const { data, isLoading } = useLicenseListQuery({
    search: query.search,
    limit: query.limit,
    current_page: query.current,
    sort_by: pascalToSnakeCase(formatColumnName(query.sortBy)),
    sort_direction: query.sortDirection,
  });

  const editHandler = (id: string | number) => {
    navigate(ROUTE.EDIT_SAAS_TOOL, { state: { id } });
  };


  const schedulerHandler = async () => {
    const response = (await syncUserApi()) as any;
    if (response?.data?.success) {
      toast.success(response?.data?.message);
    } else {
      toast.error(response?.data?.message);
    }
  };

  const redirectHandler = (name: string) => {
    switch (name) {
      case "Okta":
        navigate(ROUTE.OKTA_INVITED_USER_LIST);
        break;
      case "GitLab":
        navigate(ROUTE.GITLAB_INVITED_USER_LIST);
        break;
      case "Confluence":
        navigate(ROUTE.CONFLUENCE_INVITED_USER_LIST);
        break;
      case "Atlassian":
        navigate(ROUTE.ATLASSIAN_INVITED_USER_LIST);
        break;
      case "Miro":
        navigate(ROUTE.MIRO_INVITED_USER_LIST);
        break;
      case "Youtrack":
        navigate(ROUTE.YOUTRACK_INVITED_USER_LIST);
        break;
      case "Box":
        navigate(ROUTE.BOX_INVITED_USER_LIST);
        break;
      case "GCP":
        navigate(ROUTE.GCP_INVITED_USER_LIST);
        break;
      case "SOPHOS MDR":
        navigate(ROUTE.SOPHOS_INVITED_USER_LIST);
        break;
      default:
        toast.warn(SOMETHING_WENT_WRONG);
        break;
    }
  };

  return (
    <div className={pageClassName}>
      <Breadcrumb />
      <p className={`${pageClassName}__title`}>
        {PAGE_NAME.LICENSE_MANAGEMENT}
      </p>
      <div className={`${pageClassName}__container`}>
        <div className={`${pageClassName}__header`}>
          <div className={`${pageClassName}__search`}>
            <Input
              placeholder="Search"
              onChange={searchHandler}
              value={search}
            />
          </div>
          <div className={`${pageClassName}__action`}>
            {hasPermission(PERMISSIONS.SYNC_SAAS_USER.title) && (
              <button
                className={`${pageClassName}__button ${pageClassName}__button--scheduler`}
                onClick={schedulerHandler}
                disabled={syncLoading}
              >
                Update License Info
              </button>
            )}
            {hasPermission(PERMISSIONS.LICENSE_CREATE.title) && (
              <button
                className={`${pageClassName}__button`}
                onClick={() => navigate(ROUTE.ADD_SAAS_TOOL)}
                disabled={syncLoading}
              >
                Add Saas Tool
              </button>
            )}
          </div>
        </div>
        <div className={`${pageClassName}__table`}>
          <Table
            header={tableHeader}
            data={data?.result?.licenses ?? []}
            loading={syncLoading || isLoading}
            firstLoad={isLoading}
            sortHandler={sortHandler}
            sortBy={query.sortBy}
            minWidth="1440"
            startIndex={data?.result?.from}
            editHandler={editHandler}
            deleteHandler={redirectHandler}
            viewURL={ROUTE.LICENSE_MANAGEMENT}
            cursorPointer
          />
        </div>
      </div>
    </div>
  );
};

export default LicenseList;
